// 任务统计柱状图（SVG 手画，不引图表库）：每段三根并排细柱「新建 / 完成 / 卡住·失败」，数量为 0 的段也占位；
// 悬停整段（命中区比柱子大）显示该段三项数字；横轴标签过密时间隔显示；颜色取 global.css 的 --chart-* tokens
import { useEffect, useRef, useState } from "react";
import type { StatsBucket } from "../lib/types";
import { fmtDur } from "../lib/format";
import { labelStep, niceTicks } from "../lib/taskQuery";

export const SERIES = [
  { key: "created", label: "新建", color: "var(--chart-created)" },
  { key: "done", label: "完成", color: "var(--chart-done)" },
  { key: "troubled", label: "卡住/失败/取消", color: "var(--chart-troubled)" },
] as const;

const H = 240;
const PAD_T = 12;
const PAD_B = 30;
const PAD_L = 36;
const PAD_R = 8;

/** 顶端 r 圆角、底端贴基线的柱子路径 */
function barPath(x: number, y: number, w: number, h: number): string {
  const r = Math.min(3, w / 2, h);
  const b = y + h;
  return `M${x},${b}V${y + r}Q${x},${y} ${x + r},${y}H${x + w - r}Q${x + w},${y} ${x + w},${y + r}V${b}Z`;
}

export function StatsChart({ buckets }: { buckets: StatsBucket[] }) {
  const wrap = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(800);
  const [hover, setHover] = useState<number | null>(null);

  useEffect(() => {
    const el = wrap.current;
    if (!el) return;
    const update = () => setWidth(Math.max(280, el.clientWidth));
    update();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const plotW = width - PAD_L - PAD_R;
  const plotH = H - PAD_T - PAD_B;
  const n = buckets.length;
  const slot = n ? plotW / n : plotW;
  const maxV = Math.max(0, ...buckets.map((b) => Math.max(b.created, b.done, b.troubled)));
  const { max, step } = niceTicks(maxV);
  const y = (v: number) => PAD_T + plotH - (v / max) * plotH;
  // 三根柱子 + 段间留白；柱子之间留 1px 缝
  const gap = slot > 12 ? 1 : 0;
  const barW = Math.max(1, Math.min(14, (slot * 0.72 - gap * 2) / 3));
  const groupW = barW * 3 + gap * 2;
  const every = labelStep(n, plotW, 64);
  const ticks: number[] = [];
  for (let v = 0; v <= max; v += step) ticks.push(v);

  const hb = hover === null ? null : buckets[hover];
  const tipLeft = hover === null ? 0 : Math.min(Math.max(4, PAD_L + slot * hover + slot / 2 - 90), width - 184);

  return (
    <div className="chart-wrap stats-chart" ref={wrap} style={{ position: "relative" }}>
      <svg viewBox={`0 0 ${width} ${H}`} height={H} role="img" aria-label="新建、完成、卡住/失败任务数柱状图" data-testid="stats-chart">
        {ticks.map((v) => (
          <g key={v}>
            <line x1={PAD_L} x2={width - PAD_R} y1={y(v)} y2={y(v)} stroke="var(--line)" strokeWidth={1} />
            <text x={PAD_L - 6} y={y(v) + 4} fontSize={11} textAnchor="end" fill="var(--faint)">
              {v}
            </text>
          </g>
        ))}
        {buckets.map((b, i) => {
          const x0 = PAD_L + slot * i + (slot - groupW) / 2;
          return (
            <g
              key={b.key}
              data-testid="stats-bucket"
              data-key={b.key}
              onMouseEnter={() => setHover(i)}
              onClick={() => setHover(i)}
              onMouseLeave={() => setHover((h) => (h === i ? null : h))}
            >
              {/* 整段命中区（比柱子大），悬停时浅底高亮 */}
              <rect x={PAD_L + slot * i} y={PAD_T} width={slot} height={plotH} fill={hover === i ? "var(--panel-2)" : "transparent"} />
              {SERIES.map((s, k) => {
                const v = b[s.key];
                if (!v) return null;
                const top = y(v);
                return <path key={s.key} d={barPath(x0 + k * (barW + gap), top, barW, PAD_T + plotH - top)} fill={s.color} />;
              })}
              {i % every === 0 && (
                <text x={PAD_L + slot * i + slot / 2} y={H - 10} fontSize={11} textAnchor="middle" fill="var(--muted)">
                  {shortLabel(b.label)}
                </text>
              )}
            </g>
          );
        })}
        <line x1={PAD_L} x2={width - PAD_R} y1={PAD_T + plotH} y2={PAD_T + plotH} stroke="var(--input-line)" strokeWidth={1} />
      </svg>
      {hb && (
        <div className="chart-tip" style={{ left: tipLeft, top: 8 }} data-testid="stats-tip" role="tooltip">
          <b>{hb.label}</b>
          {SERIES.map((s) => (
            <div key={s.key} className="tip-row">
              <i style={{ background: s.color }} />
              <span>{s.label}</span>
              <b>{hb[s.key]}</b>
            </div>
          ))}
          <div className="tip-row muted">
            <span>平均用时</span>
            <b>{hb.avgMs === null ? "—" : fmtDur(hb.avgMs)}</b>
          </div>
        </div>
      )}
      <div className="legend" style={{ marginTop: 6 }} data-testid="stats-legend">
        {SERIES.map((s) => (
          <span key={s.key}>
            <i style={{ background: s.color, borderRadius: 2 }} />
            {s.label}
          </span>
        ))}
        <span>悬停柱子看该段数字</span>
      </div>
    </div>
  );
}

/** 横轴标签：周的「第41周（10.5–10.11）」只显示「10.5–10.11」，省地方；悬停里仍是完整 label */
function shortLabel(label: string): string {
  const m = /（(.+)）/.exec(label);
  return m ? m[1]! : label;
}
